import React from 'react';
import {
  hasLayoutShellReady,
  ROUTE_LOADING_REENTRY_DELAY_MS,
  hasRouteContentReady,
} from '@/components/routeLoadingBoundary';
import useDelayedVisibility from '@/components/useDelayedVisibility';

type PageOwnedLoadingVariant =
  | 'home'
  | 'dashboard'
  | 'workspace'
  | 'agents'
  | 'skills'
  | 'resources'
  | 'thread';
type GlobalShellVariant = 'payment' | 'management';
type LoadingVariant =
  | PageOwnedLoadingVariant
  | GlobalShellVariant
  | 'editor'
  | 'login';

const PAGE_OWNED_SKELETON_VARIANTS = new Set<PageOwnedLoadingVariant>([
  'home',
  'dashboard',
  'workspace',
  'agents',
  'skills',
  'resources',
  'thread',
]);

function isPageOwnedLoadingVariant(variant: LoadingVariant): variant is PageOwnedLoadingVariant {
  return PAGE_OWNED_SKELETON_VARIANTS.has(variant as PageOwnedLoadingVariant);
}

const LOADING_VARIANT_RULES: Array<{
  variant: LoadingVariant;
  match: (pathname: string) => boolean;
}> = [
  { variant: 'thread', match: (pathname) => pathname.startsWith('/thread') },
  { variant: 'editor', match: (pathname) => pathname.startsWith('/editor') },
  { variant: 'login', match: (pathname) => pathname.startsWith('/user/login') },
  { variant: 'workspace', match: (pathname) => pathname.startsWith('/workspace') },
  { variant: 'dashboard', match: (pathname) => pathname.startsWith('/dashboard') },
  { variant: 'agents', match: (pathname) => pathname.startsWith('/agents') },
  { variant: 'skills', match: (pathname) => pathname.startsWith('/skills') },
  { variant: 'resources', match: (pathname) => pathname.startsWith('/resources') },
  { variant: 'payment', match: (pathname) => pathname.startsWith('/payment') },
  { variant: 'home', match: (pathname) => pathname === '/' || pathname.startsWith('/home') },
];

/**
 * `src/loading.tsx` 发生在 React 已挂载、但 `getInitialState` 仍未完成的阶段。
 *
 * 如果这里返回 `null`，浏览器会经历：
 * 1. 启动脚本骨架已经出现；
 * 2. React 挂载后立刻把骨架替换成空白；
 * 3. 等待 `initialState` 和路由组件就绪后才再次渲染页面。
 *
 * 因此这里遵循统一的三层加载模型：
 * - `boot shell`：由 `public/scripts/loading.js` 负责兜住首屏；
 * - `route skeleton`：只保留给“页面自身没有首屏骨架”的路由；
 * - `local updating`：交给各业务页自己处理，避免重新退回整页 skeleton。
 *
 * 这里现在只兜“全局骨架仍然有必要”的少数路由：
 * - `payment / management`：页面本身还没有首屏业务骨架；
 * - `editor / login`：属于独立页，需要单独的兜底壳层。
 *
 * 其余路由（`home / dashboard / workspace / agents / skills / resources / thread`）
 * 统一改成：
 * - 冷启动：由 `public/scripts/loading.js` 负责；
 * - React 接管后：直接交给页面自身 skeleton / updating。
 *
 * 这样可以把全局加载链路真正收窄成一条“兜底支线”，
 * 避免首页或业务页已经展示了正确骨架后，又被 `src/loading.tsx` 覆盖成别的样式。
 *
 * 特例：
 * - `thread` 页面已经有自己独立的历史恢复、空态和请求态展示；
 * - 因此这里不再为 `thread` 提供任何全局 skeleton，避免和页面内语义重叠。
 */
const resolveLoadingVariant = (pathname: string): LoadingVariant => {
  return LOADING_VARIANT_RULES.find((rule) => rule.match(pathname))?.variant ?? 'management';
};

const Bar: React.FC<{ width: string; height: string; radius?: string; className?: string }> = ({
  width,
  height,
  radius,
  className,
}) => (
  <span
    className={['app-shell-bar', className].filter(Boolean).join(' ')}
    style={{ width, height, borderRadius: radius || '999px' }}
  />
);

const MenuItem: React.FC<{ width: string; active?: boolean }> = ({ width, active }) => (
  <div className={['app-shell-menu-item', active ? 'is-active' : ''].filter(Boolean).join(' ')}>
    <Bar width="18px" height="18px" radius="8px" />
    <Bar width={width} height="14px" />
  </div>
);

const SidebarSkeleton: React.FC<{ activeKey: 'home' }> = ({ activeKey }) => {
  const menu = [
    { key: 'home', width: '96px' },
    { key: 'dashboard', width: '78px' },
    { key: 'workspace', width: '88px' },
    { key: 'agents', width: '72px' },
    { key: 'skills', width: '84px' },
    { key: 'resources', width: '92px' },
  ] as const;

  return (
    <aside className="app-shell-sider" aria-hidden="true">
      <div className="app-shell-brand">
        <span className="app-shell-logo" />
        <Bar width="118px" height="16px" />
      </div>

      <div className="app-shell-menu">
        {menu.map((item) => (
          <MenuItem key={item.key} width={item.width} active={item.key === activeKey} />
        ))}
      </div>

      <div className="app-shell-footer">
        <Bar width="100%" height="54px" radius="18px" />
        <Bar width="100%" height="44px" radius="14px" />
      </div>
    </aside>
  );
};

const PageHeaderSkeleton: React.FC<{ titleWidth: string; subtitleWidth: string; actionWidth?: string }> = ({
  titleWidth,
  subtitleWidth,
  actionWidth,
}) => (
  <div className="app-shell-page-header">
    <div className="app-shell-header-copy">
      <Bar width={titleWidth} height="24px" />
      <Bar width={subtitleWidth} height="14px" />
    </div>
    {actionWidth ? <Bar width={actionWidth} height="36px" radius="12px" /> : null}
  </div>
);

const StatusChipSkeleton: React.FC<{ text: string }> = ({ text }) => (
  <div className="app-shell-status">
    <span className="app-shell-spinner" />
    <span>{text}</span>
  </div>
);

const ListCardSkeleton: React.FC<{ title: string; descA: string; descB: string }> = ({ title, descA, descB }) => (
  <div className="app-shell-list-card">
    <Bar width={title} height="14px" />
    <Bar width={descA} height="12px" />
    <Bar width={descB} height="12px" />
  </div>
);

const EditorShellSkeleton: React.FC = () => (
  <div className="app-shell-standalone app-shell-editor">
    <div className="app-shell-editor-shell">
      <div className="app-shell-thread-head">
        <div className="app-shell-toolbar">
          <Bar width="86px" height="32px" radius="10px" />
          <Bar width="72px" height="32px" radius="10px" />
          <Bar width="66px" height="32px" radius="10px" />
        </div>
        <div className="app-shell-toolbar">
          <Bar width="36px" height="32px" radius="10px" />
          <Bar width="36px" height="32px" radius="10px" />
          <Bar width="88px" height="32px" radius="10px" />
        </div>
      </div>
      <div className="app-shell-editor-body">
        <div className="app-shell-editor-side">
          <ListCardSkeleton title="56%" descA="84%" descB="66%" />
          <ListCardSkeleton title="62%" descA="82%" descB="58%" />
          <ListCardSkeleton title="48%" descA="78%" descB="54%" />
        </div>
        <div className="app-shell-editor-canvas">
          <Bar width="100%" height="100%" radius="24px" />
        </div>
        <div className="app-shell-editor-side">
          <Bar width="68%" height="16px" />
          <Bar width="100%" height="120px" radius="16px" />
          <Bar width="52%" height="16px" />
          <Bar width="100%" height="180px" radius="16px" />
        </div>
      </div>
    </div>
  </div>
);

const CenteredCardSkeleton: React.FC<{ titleWidth: string; descWidth: string }> = ({ titleWidth, descWidth }) => (
  <div className="app-shell-standalone app-shell-centered">
    <div className="app-shell-auth-card">
      <Bar width={titleWidth} height="24px" />
      <Bar width={descWidth} height="14px" />
      <div className="app-shell-field">
        <Bar width="64px" height="10px" />
        <Bar width="100%" height="42px" radius="12px" />
      </div>
      <div className="app-shell-field">
        <Bar width="72px" height="10px" />
        <Bar width="100%" height="42px" radius="12px" />
      </div>
      <Bar width="100%" height="42px" radius="12px" />
    </div>
  </div>
);

const PaymentShellSkeleton: React.FC = () => (
  <>
    <PageHeaderSkeleton titleWidth="126px" subtitleWidth="min(420px, 70vw)" />
    <StatusChipSkeleton text="正在准备支付与订单信息" />
    <section className="app-shell-panel app-shell-payment-panel">
      <div className="app-shell-panel-head">
        <Bar width="136px" height="18px" />
        <Bar width="220px" height="12px" />
      </div>
      <div className="app-shell-detail-body">
        <div className="app-shell-section">
          <Bar width="82px" height="12px" />
          <Bar width="100%" height="84px" radius="16px" />
        </div>
        <div className="app-shell-section">
          <Bar width="96px" height="12px" />
          <Bar width="100%" height="52px" radius="12px" />
          <Bar width="100%" height="52px" radius="12px" />
        </div>
      </div>
    </section>
  </>
);

const ManagementShellSkeleton: React.FC = () => (
  <>
    <PageHeaderSkeleton titleWidth="112px" subtitleWidth="min(460px, 70vw)" actionWidth="108px" />
    <StatusChipSkeleton text="正在准备页面内容" />
    <section className="app-shell-panel">
      <div className="app-shell-panel-head app-shell-panel-head-row">
        <div className="app-shell-header-copy">
          <Bar width="156px" height="18px" />
          <Bar width="220px" height="12px" />
        </div>
        <Bar width="108px" height="36px" radius="12px" />
      </div>
      <div className="app-shell-detail-body">
        <div className="app-shell-section">
          <Bar width="88px" height="12px" />
          <Bar width="100%" height="180px" radius="18px" />
        </div>
        <div className="app-shell-section">
          <Bar width="94px" height="12px" />
          <div className="app-shell-section-row">
            <div className="app-shell-field">
              <Bar width="60px" height="10px" />
              <Bar width="100%" height="42px" radius="12px" />
            </div>
            <div className="app-shell-field">
              <Bar width="72px" height="10px" />
              <Bar width="100%" height="42px" radius="12px" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

/**
 * 路由级 loading 统一收敛到注册表，避免继续在入口组件里堆三元分支。
 */
const SHELL_COMPONENT_BY_VARIANT: Record<GlobalShellVariant, React.FC> = {
  payment: PaymentShellSkeleton,
  management: ManagementShellSkeleton,
};

const RouteLoadingSkeleton: React.FC<{ variant: LoadingVariant }> = ({ variant }) => {
  if (variant === 'editor') return <EditorShellSkeleton />;
  if (variant === 'login') return <CenteredCardSkeleton titleWidth="148px" descWidth="220px" />;
  const layoutVariant = variant as GlobalShellVariant;
  const ShellContent = SHELL_COMPONENT_BY_VARIANT[layoutVariant];

  return (
    <div className="app-shell-main" aria-hidden="true">
      <ShellContent />
    </div>
  );
};

/**
 * 仅在 ProLayout 外壳尚未挂起的冷启动阶段使用整页 app shell。
 *
 * 一旦外壳已经存在，就必须退回 `RouteLoadingSkeleton`，
 * 否则右侧内容区里会再次出现一层假的 sider。
 */
const FullAppShellLoading: React.FC<{ variant: LoadingVariant }> = ({ variant }) => {
  if (variant === 'editor') return <EditorShellSkeleton />;
  if (variant === 'login') return <CenteredCardSkeleton titleWidth="148px" descWidth="220px" />;

  const layoutVariant = variant as GlobalShellVariant;
  const ShellContent = SHELL_COMPONENT_BY_VARIANT[layoutVariant];

  return (
    <div className="app-shell-layout" aria-hidden="true">
      <SidebarSkeleton activeKey="home" />
      <main className="app-shell-main">
        <ShellContent />
      </main>
    </div>
  );
};

const styles = `
  html, body, #root {
    height: 100%;
  }

  body {
    margin: 0;
    background: #f5f7fa;
  }

  .app-shell-layout,
  .app-shell-main,
  .app-shell-standalone {
    box-sizing: border-box;
  }

  .app-shell-layout,
  .app-shell-standalone {
    min-height: 100%;
  }

  .app-shell-layout {
    display: grid;
    grid-template-columns: 248px minmax(0, 1fr);
    background: #f5f7fa;
  }

  .app-shell-sider {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 18px 14px 16px;
    background: linear-gradient(180deg, #f7f8fa 0%, #f5f7fa 100%);
    border-right: 1px solid #eceff3;
  }

  .app-shell-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px 14px;
  }

  .app-shell-logo {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    background: linear-gradient(135deg, #dbeafe 0%, #e8f0ff 48%, #eef4ff 100%);
    box-shadow: inset 0 0 0 1px rgba(22, 119, 255, 0.08);
  }

  .app-shell-menu,
  .app-shell-footer,
  .app-shell-panel-body,
  .app-shell-detail-body,
  .app-shell-editor-side,
  .app-shell-thread-body,
  .app-shell-thread-preview {
    display: flex;
    flex-direction: column;
  }

  .app-shell-menu,
  .app-shell-footer {
    gap: 10px;
  }

  .app-shell-footer {
    margin-top: auto;
    padding-top: 8px;
  }

  .app-shell-main {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }

  .app-shell-page-header,
  .app-shell-panel-head-row,
  .app-shell-thread-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .app-shell-header-copy,
  .app-shell-field,
  .app-shell-panel-head,
  .app-shell-list-card,
  .app-shell-section,
  .app-shell-table-title,
  .app-shell-auth-card {
    display: flex;
    flex-direction: column;
  }

  .app-shell-header-copy,
  .app-shell-field {
    gap: 8px;
  }

  .app-shell-page-header {
    padding-top: 4px;
  }

  .app-shell-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .app-shell-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.42);
  }

  .app-shell-menu-item.is-active {
    background: rgba(17, 24, 39, 0.05);
  }

  .app-shell-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    align-self: flex-start;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.78);
    border: 1px solid rgba(22, 119, 255, 0.08);
    color: #667085;
    font-size: 13px;
    line-height: 20px;
  }

  .app-shell-spinner {
    width: 16px;
    height: 16px;
    border-radius: 999px;
    border: 2px solid rgba(22, 119, 255, 0.18);
    border-top-color: #1677ff;
    animation: app-shell-rotate 1s linear infinite;
  }

  .app-shell-bar {
    position: relative;
    display: block;
    overflow: hidden;
    background: linear-gradient(90deg, #eef2f7 0%, #f7f9fb 45%, #eef2f7 100%);
    background-size: 200% 100%;
    animation: app-shell-shimmer 1.55s ease infinite;
  }

  .app-shell-metric-card,
  .app-shell-panel,
  .app-shell-auth-card,
  .app-shell-thread-shell,
  .app-shell-editor-shell {
    border: 1px solid #e8edf3;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .app-shell-metric-card {
    gap: 12px;
    padding: 16px 18px;
    border-radius: 18px;
  }

  .app-shell-panel {
    overflow: hidden;
    border-radius: 24px;
  }

  .app-shell-panel-head {
    gap: 10px;
    padding: 18px 20px 16px;
    border-bottom: 1px solid #edf1f5;
    background: rgba(250, 251, 252, 0.88);
  }

  .app-shell-panel-body,
  .app-shell-detail-body {
    gap: 10px;
    padding: 16px;
  }

  .app-shell-detail-body {
    gap: 16px;
    padding: 20px 24px 26px;
  }

  .app-shell-list-card,
  .app-shell-section {
    gap: 10px;
    padding: 14px;
    border: 1px solid #edf1f5;
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(250, 251, 252, 0.9) 0%, rgba(255, 255, 255, 0.96) 100%);
  }

  .app-shell-section {
    padding: 18px;
    gap: 12px;
  }

  .app-shell-section-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .app-shell-standalone {
    background: #f5f7fa;
  }

  .app-shell-centered,
  .app-shell-thread,
  .app-shell-editor {
    display: flex;
    min-height: 100%;
    padding: 16px;
  }

  .app-shell-centered {
    align-items: center;
    justify-content: center;
  }

  .app-shell-auth-card {
    width: min(420px, 100%);
    gap: 16px;
    padding: 28px;
    border-radius: 24px;
  }

  .app-shell-thread-shell {
    width: 100%;
    min-height: calc(100vh - 32px);
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) 16px minmax(320px, 0.82fr);
    border-radius: 24px;
    overflow: hidden;
  }

  .app-shell-thread-left,
  .app-shell-thread-right {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  .app-shell-thread-left {
    background: rgba(250, 251, 252, 0.82);
  }

  .app-shell-thread-body,
  .app-shell-thread-preview {
    gap: 14px;
    padding: 20px;
  }

  .app-shell-thread-message {
    max-width: min(78%, 720px);
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    border: 1px solid #edf1f5;
    border-radius: 18px;
    background: #ffffff;
  }

  .app-shell-thread-message.user {
    align-self: flex-end;
    background: linear-gradient(180deg, rgba(235, 243, 255, 0.84) 0%, rgba(245, 249, 255, 0.92) 100%);
  }

  .app-shell-thread-sender {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px 20px 20px;
    border-top: 1px solid #edf1f5;
    background: rgba(255, 255, 255, 0.92);
  }

  .app-shell-thread-sender-foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .app-shell-thread-divider {
    background: linear-gradient(90deg, rgba(232, 237, 243, 0) 0%, rgba(232, 237, 243, 1) 50%, rgba(232, 237, 243, 0) 100%);
  }

  .app-shell-editor-shell {
    width: 100%;
    min-height: calc(100vh - 32px);
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    overflow: hidden;
  }

  .app-shell-editor-body {
    flex: 1;
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr) 320px;
    gap: 16px;
    padding: 16px;
    background: rgba(245, 247, 250, 0.7);
  }

  .app-shell-editor-canvas,
  .app-shell-editor-side {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    min-height: 0;
  }

  .app-shell-editor-canvas {
    padding: 12px;
    border-radius: 24px;
    border: 1px solid #e8edf3;
    background: #ffffff;
  }

  .app-shell-payment-panel {
    width: min(760px, 100%);
  }

  @keyframes app-shell-shimmer {
    0% {
      background-position: 100% 50%;
    }

    100% {
      background-position: 0 50%;
    }
  }

  @keyframes app-shell-rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1180px) {
    .app-shell-editor-body {
      grid-template-columns: 240px minmax(0, 1fr) 280px;
    }
  }

  @media (max-width: 960px) {
    .app-shell-layout {
      grid-template-columns: 1fr;
    }

    .app-shell-sider,
    .app-shell-thread-right,
    .app-shell-thread-divider,
    .app-shell-editor-side:last-child {
      display: none;
    }

    .app-shell-thread-shell,
    .app-shell-editor-body {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 720px) {
    .app-shell-main,
    .app-shell-thread,
    .app-shell-editor {
      padding: 16px;
    }

    .app-shell-page-header,
    .app-shell-panel-head-row,
    .app-shell-thread-head {
      flex-direction: column;
      align-items: flex-start;
    }

    .app-shell-section-row {
      grid-template-columns: 1fr;
    }
  }
`;

const Loading: React.FC = () => {
  const pathname = typeof window === 'undefined' ? '/' : window.location.pathname;
  const variant = resolveLoadingVariant(pathname);
  const hasMountedLayoutShell = hasLayoutShellReady();
  /**
   * 全局 loading 只应该兜住两类场景：
   * - 冷启动首屏：立即显示，避免白屏；
   * - 持续时间足够长的高层 fallback：延迟后再显示，避免压过已经出现的业务骨架。
   */
  const hasRenderedRouteContent = hasRouteContentReady(pathname);
  const showDelayedReentryShell = useDelayedVisibility(
    hasRenderedRouteContent,
    ROUTE_LOADING_REENTRY_DELAY_MS,
  );
  /**
   * 页面自带骨架的路由完全退出 `src/loading.tsx`：
   * - 冷启动首屏由 `public/scripts/loading.js` 负责；
   * - React 接管后直接交给页面自身的业务 skeleton / updating。
   *
   * 这样可以彻底切断“boot overlay → 全局 route skeleton → 页面骨架”这条多段链路，
   * 只保留“boot overlay → 页面骨架”两段，避免首页或业务页右侧继续闪出别的骨架。
   *
   * 这里把早退放在 hooks 之后，保证 hook 调用顺序在每次渲染中保持稳定。
   */
  if (isPageOwnedLoadingVariant(variant)) {
    return null;
  }
  const shouldRenderShell = !hasRenderedRouteContent || showDelayedReentryShell;
  /**
   * 双模式切换：
   * - layout 未挂起：显示整页 app shell（含左侧导航骨架）；
   * - layout 已挂起：只显示右侧内容区骨架，避免右侧再嵌一层假 sider。
   */
  const shellNode = hasMountedLayoutShell
    ? <RouteLoadingSkeleton variant={variant} />
    : <FullAppShellLoading variant={variant} />;

  return (
    <>
      <style>{styles}</style>
      {shouldRenderShell ? shellNode : null}
    </>
  );
};

export default Loading;
